import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isMobileApp } from "../lib/platform";

const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.4 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.6 0 19.5-8 19.5-19.5 0-1.2-.1-2.3-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.4 29.1 4.5 24 4.5 16.3 4.5 9.7 8.7 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.1-7.1 2.1-5.3 0-9.7-3.4-11.3-8L6.3 32.5C9.6 38.7 16.2 43.5 24 43.5z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.1-2.1 3.9-3.9 5.4l6.1 5c4.3-4 6.5-9.9 6.5-16.4 0-1.2-.1-2.3-.4-3.5z" />
    </svg>
);

export default function Login() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fromLoc = location.state?.from;
    // Preserve the full URL the user was trying to reach: /app?file=abc#row5
    const from = fromLoc
        ? `${fromLoc.pathname || "/app"}${fromLoc.search || ""}${fromLoc.hash || ""}`
        : "/app";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    // 2FA challenge state — when the backend returns requires_2fa, we swap
    // to a code-entry screen instead of submitting cookies right away.
    const [challenge, setChallenge] = useState(null); // { challenge_token, email_hint, expires_in_seconds }
    const [code, setCode] = useState("");
    const [resending, setResending] = useState(false);

    const completeLogin = (data) => {
        setUser(data);
        if (!data.is_paid && data.role !== "admin") {
            if (isMobileApp()) {
                navigate("/unlock", { replace: true });
                return true;
            }
            // Free users go to the editor in viewer mode. They'll see the
            // upsell banner there if they want Pro.
            toast.success("Welcome back");
            navigate("/app", { replace: true });
            return true;
        }
        toast.success("Welcome back");
        navigate(from, { replace: true });
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.requires_2fa) {
                setChallenge({
                    challenge_token: data.challenge_token,
                    email_hint: data.email_hint,
                });
                toast.message("Check your email for a sign-in code", { description: `Sent to ${data.email_hint}` });
                return;
            }
            completeLogin(data);
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            const msg = formatApiErrorDetail(detail);
            if (status === 429) {
                setError(msg || "Too many attempts. Try again in a few minutes.");
            } else if (err.message === "Network Error") {
                setError("Couldn't reach the server. Check your internet and try again.");
            } else {
                setError(msg || "Login failed");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/2fa-verify", {
                challenge_token: challenge.challenge_token,
                code: code.trim(),
            });
            completeLogin(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = formatApiErrorDetail(detail);
            setError(msg || "Couldn't verify that code");
            // If the session expired, bounce back to password entry.
            if (typeof msg === "string" && msg.toLowerCase().includes("expired")) {
                setChallenge(null);
                setCode("");
                setPassword("");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!challenge || resending) return;
        setResending(true);
        setError("");
        try {
            const { data } = await api.post("/auth/2fa-resend", { challenge_token: challenge.challenge_token });
            setChallenge({ challenge_token: data.challenge_token, email_hint: data.email_hint });
            toast.success("New code sent");
            setCode("");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(formatApiErrorDetail(detail) || "Couldn't resend the code");
        } finally {
            setResending(false);
        }
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/app";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <>
            <Helmet><title>Sign in — OpenCSV</title></Helmet>
            <section className="border-b border-black/10 min-h-[80vh] grid md:grid-cols-2">
                <div className="hidden md:flex flex-col justify-between p-12 bg-ink text-white">
                    <div>
                        <h1 className="font-display font-black tracking-tighter text-5xl leading-[0.95]">
                            Welcome<br />back.
                        </h1>
                        <p className="mt-5 text-white/70 max-w-sm">Open your CSVs from anywhere. Lifetime access. Zero friction.</p>
                    </div>
                    <p className="text-xs text-white/40 ui-heading uppercase tracking-widest">/ opencsv.io</p>
                </div>
                <div className="p-8 md:p-16 flex items-center">
                    <div className="w-full max-w-sm mx-auto">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ sign in</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl mb-6">{challenge ? "One more step." : "Hey, you're back."}</h2>

                        {challenge ? (
                            <form onSubmit={handleVerifyCode} className="space-y-4" data-testid="login-2fa-form">
                                <p className="text-sm text-ink-soft">
                                    We emailed a 6-digit code to <span className="font-mono text-ink">{challenge.email_hint}</span>. It expires in 10 minutes.
                                </p>
                                <div>
                                    <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Sign-in code</label>
                                    <input
                                        type="text"
                                        required
                                        autoFocus
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                        className="mt-1 w-full border border-ink/20 px-3 py-3 bg-white focus-cobalt font-mono tracking-[0.4em] text-center text-xl"
                                        placeholder="000000"
                                        data-testid="login-2fa-code-input"
                                    />
                                </div>
                                {error && <div className="text-sm text-signal-red border border-signal-red/30 bg-signal-red/5 px-3 py-2" data-testid="login-2fa-error">{error}</div>}
                                <button type="submit" disabled={loading || code.length !== 6} className="btn-brutal w-full bg-cobalt hover:bg-cobalt-hover text-white px-4 py-3 ui-heading font-semibold border border-cobalt disabled:opacity-60" data-testid="login-2fa-submit-button">
                                    {loading ? "Verifying…" : "Verify and sign in"}
                                </button>
                                <div className="flex items-center justify-between text-xs text-ink-soft pt-1">
                                    <button type="button" onClick={handleResend} disabled={resending} className="text-cobalt hover:underline disabled:opacity-60" data-testid="login-2fa-resend">
                                        {resending ? "Sending…" : "Resend code"}
                                    </button>
                                    <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="text-ink-muted hover:underline" data-testid="login-2fa-cancel">
                                        Use a different account
                                    </button>
                                </div>
                            </form>
                        ) : (
                          <>
                        {!isMobileApp() && (
                            <>
                                <button onClick={handleGoogle} className="btn-brutal w-full inline-flex items-center justify-center gap-3 border border-ink/20 px-4 py-3 bg-white ui-heading text-sm font-semibold mb-4" data-testid="login-google-button">
                                    <GoogleIcon /> Continue with Google
                                </button>
                                <div className="flex items-center gap-3 mb-4 text-[11px] uppercase tracking-widest text-ink-muted">
                                    <div className="flex-1 h-px bg-ink/10" /> or with email <div className="flex-1 h-px bg-ink/10" />
                                </div>
                            </>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
                            <div>
                                <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Email</label>
                                <input
                                    type="email"
                                    required
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    autoComplete="email"
                                    inputMode="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.trim())}
                                    className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt"
                                    data-testid="login-email-input"
                                />
                            </div>
                            <div>
                                <div className="flex justify-between items-baseline">
                                    <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Password</label>
                                    <Link to="/forgot-password" className="text-[11px] text-cobalt hover:underline" data-testid="login-forgot-link">Forgot?</Link>
                                </div>
                                <input
                                    type="password"
                                    required
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt"
                                    data-testid="login-password-input"
                                />
                            </div>
                            {error && <div className="text-sm text-signal-red border border-signal-red/30 bg-signal-red/5 px-3 py-2" data-testid="login-error">{error}</div>}
                            <button type="submit" disabled={loading} className="btn-brutal w-full bg-cobalt hover:bg-cobalt-hover text-white px-4 py-3 ui-heading font-semibold border border-cobalt disabled:opacity-60" data-testid="login-submit-button">
                                {loading ? "Signing in…" : "Sign in"}
                            </button>
                        </form>
                        <p className="mt-5 text-sm text-ink-soft">
                            New here? <Link to="/signup" className="text-cobalt hover:underline" data-testid="login-signup-link">Create an account</Link>
                        </p>
                          </>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
